import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { blogPosts } from '../data/mock';
import { SEO } from './SEO';
import { buildBreadcrumb } from '../lib/seo-schemas';

export const BlogIndexPage = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const blogListSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ElevatED Blog & Resources',
    description:
      'Guides, study permit tips, and career college insights for students applying to colleges in British Columbia, Canada.',
    blogPost: blogPosts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      datePublished: p.date,
      url: `https://elevatededucation.ca/blog/${p.slug}`,
      image: p.image,
    })),
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title="Blog & Resources – Canadian Education Insights"
        description="Read expert guides on BC career colleges, Canadian study permits, healthcare programs, and education funding from ElevatED's advisors."
        path="/blog"
        schema={[
          blogListSchema,
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12" data-testid="blog-index-header">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">
            Blog & Resources
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-gray-900">
            Canadian Education Insights & Guides
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Expert advice on applying to BC career colleges, provincial student aid, and choosing the right program.
          </p>
        </div>

        {/* Posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="relative h-56 overflow-hidden bg-gray-200">
                <img
                  src={post.image}
                  alt={`${post.title} — ElevatED blog`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                  <time dateTime={post.date}>
                    {new Date(post.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </time>
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {post.readTime}
                  </span>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">
                  {post.title}
                </h2>

                <p className="text-gray-600 mb-4 leading-relaxed">{post.excerpt}</p>

                <span className="text-blue-600 font-semibold inline-flex items-center group-hover:translate-x-1 transition-transform duration-200">
                  Read article
                  <ArrowRight className="ml-2 w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-10 text-white text-center shadow-xl">
          <h2 className="text-3xl font-bold mb-3">Have a question we haven't covered?</h2>
          <p className="text-lg opacity-90 mb-6">
            Book a free consultation with an ElevatED advisor.
          </p>
          <Link to="/#contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" data-testid="blog-cta-consult">
              Book Free Consultation
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
